/**
 * Static Variants Configuration Example
 *
 * This file demonstrates how to configure static variants for conditional content rendering.
 * Each variant defined here will be pre-rendered at build time to static HTML files,
 * enabling hydration-safe conditional rendering based on cookie values.
 *
 * The system supports:
 * - Multi-language rendering (each variant generated per supported locale)
 * - Complex conditional logic (multiple cookie conditions with various operators)
 * - Fallback handling (default variants when no conditions match)
 * - Edge-side content selection without hydration mismatches
 *
 * Generated output: public/prebuilt-static-variants/{locale}/{variant-id}.html
 */

import { AllAccessYearly } from 'cf-banners/components/static/all-access-yearly'
import { AllAccessYearlyTrial } from 'cf-banners/components/static/all-access-yearly-trial'
import { AllAccessYearlyExtend } from 'cf-banners/components/static/all-access-yearly-extend'
import { UndoCancellationBanner } from 'cf-banners/components/static/undo-cancellation'
import { SubscriptionOnHold } from 'cf-banners/components/static/subscription-on-hold'
import { StudioAiYearlyTrial } from 'cf-banners/components/static/studio-ai-yearly-trial'
import type { Language } from 'codegen/types'
import { CurrencyCode } from 'codegen/types'
import {
  COUNTRY_COOKIE_NAME,
  CURRENCY_COOKIE_NAME,
  ALL_ACCESS_NEXT_PAYMENT_DATE_COOKIE_NAME,
} from 'constants/cookies'
import objectPath from 'object-path'
import cloneDeep from 'lodash.clonedeep'
import {
  IN_MONTHLY,
  IN_ON_HOLD_SUBSCRIPTION_ALL_ACCESS,
  IN_ON_HOLD_SUBSCRIPTION_STUDIO_AI,
  IN_ON_HOLD_SUBSCRIPTION_COINS,
  IN_PENDING_CANCEL,
  IN_YEARLY,
  IN_YEARLY_TRIAL,
  LEGACY_PENDING_CANCEL,
  LOGGED_IN,
  LOGGED_OUT,
  NL_COUNTRY,
  NOT_IN_MONTHLY,
  NOT_NL_COUNTRY,
  STUDIO_AI_IN_PENDING_CANCEL,
  NOT_IN_STUDIO_AI_ACTIVE_OR_TRIAL,
  COINS_IN_PENDING_CANCEL,
} from '../static-variants-renderer/utils/conditions'

import bannersData from './banners.json'
import type { Condition, Variant, VariantsConfig } from '../static-variants-renderer/types'

// `banners.json` only contains translations for the languages below. The GraphQL
// `Language` enum is a superset, so narrow to a supported key before indexing.
const SUPPORTED_LANGUAGES = ['de', 'en', 'es', 'fr', 'it', 'nl', 'pl', 'pt'] as const
type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number]

function toSupportedLanguage(locale: Language): SupportedLanguage {
  return (SUPPORTED_LANGUAGES as readonly string[]).includes(locale)
    ? (locale as SupportedLanguage)
    : 'en'
}

function getVariants(locale: Language): Variant[] {
  const lang = toSupportedLanguage(locale)
  const variants: Variant[] = []

  // Add legacy variants
  // oxlint-disable-next-line unicorn/no-immediate-mutation
  variants.push({
    id: `no-banner`,
    content: <div />,
    conditions: [LOGGED_IN, LEGACY_PENDING_CANCEL],
  })

  // Add variants for each currency and country combination
  for (const currency of CURRENCIES) {
    // Add all access subscription on hold variant (no country variant)
    variants.push({
      id: `on_hold_subscription-all-access-${currency.code}`,
      content: <SubscriptionOnHold banner={bannersData.onHoldSubscriptionBanner[lang]} />,
      conditions: [LOGGED_IN, IN_ON_HOLD_SUBSCRIPTION_ALL_ACCESS, withCurrency(currency.code)],
    })

    // Add studio ai subscription on hold variant (no country variant)
    variants.push({
      id: `on_hold_subscription-studio-ai-${currency.code}`,
      content: <SubscriptionOnHold banner={bannersData.onHoldSubscriptionBanner[lang]} />,
      conditions: [LOGGED_IN, IN_ON_HOLD_SUBSCRIPTION_STUDIO_AI, withCurrency(currency.code)],
    })

    // Add coins subscription on hold variant (no country variant)
    variants.push({
      id: `on_hold_subscription-coins-${currency.code}`,
      content: <SubscriptionOnHold banner={bannersData.onHoldSubscriptionBanner[lang]} />,
      conditions: [LOGGED_IN, IN_ON_HOLD_SUBSCRIPTION_COINS, withCurrency(currency.code)],
    })

    // Add pending cancel variant for all access subscription (no country variant)
    variants.push({
      id: `undo_cancellation-${currency.code}`,
      content: (
        <UndoCancellationBanner
          subscriptionType="all-access"
          banner={bannersData.undoCancellationBanner[lang]}
        />
      ),
      conditions: [LOGGED_IN, IN_PENDING_CANCEL, withCurrency(currency.code)],
    })

    // Add pending cancel variant for studio ai (no country variant)
    variants.push({
      id: `studio_ai_undo_cancellation-${currency.code}`,
      content: (
        <UndoCancellationBanner
          subscriptionType="studio-ai"
          banner={bannersData.studio_ai_undoCancellationBanner[lang]}
        />
      ),
      conditions: [LOGGED_IN, STUDIO_AI_IN_PENDING_CANCEL, withCurrency(currency.code)],
    })

    // Add pending cancel variant for coins subscription (no country variant)
    variants.push({
      id: `coins_undo_cancellation-${currency.code}`,
      content: (
        <UndoCancellationBanner
          subscriptionType="coins"
          banner={bannersData.coins_undoCancellationBanner[lang]}
        />
      ),
      conditions: [LOGGED_IN, COINS_IN_PENDING_CANCEL, withCurrency(currency.code)],
    })

    // Add variants for each country
    for (const country of COUNTRIES) {
      //Apply text replacements for currency and prices in banners
      //================================START================================//
      const allAccessYearlyTrialBanner = overwriteBannerWithData(
        bannersData.allAccessYearlyTrialBanner[lang],
        [
          {
            deepKey: 'buttonText',
            value: bannersData.allAccessYearlyTrialBanner[lang].buttonText.replace(
              /{currency}/gi,
              currency.symbol,
            ),
          },
          {
            deepKey: 'features.1',
            value: bannersData.allAccessYearlyTrialBanner[lang].features[1]
              .replace(/{currency}/gi, currency.symbol)
              .replace(/{price}/gi, country === 'NL' ? '4.74' : '3.99')
              .replace(/{fullPrice}/gi, country === 'NL' ? '35.09' : '29'),
          },
        ],
      )

      const allAccessYearlyBanner = overwriteBannerWithData(
        bannersData.allAccessYearlyBanner[lang],
        [
          {
            deepKey: 'headline',
            value: bannersData.allAccessYearlyBanner[lang].headline
              .replace(/{currency}/gi, currency.symbol)
              .replace(/{price}/gi, country === 'NL' ? '4.74' : '3.99'),
          },
          {
            deepKey: 'features.0',
            value: bannersData.allAccessYearlyBanner[lang].features[0]
              .replace(/{currency}/gi, currency.symbol)
              .replace(/{price}/gi, country === 'NL' ? '4.74' : '3.99')
              .replace(/{fullPrice}/gi, country === 'NL' ? '56.87' : '47'),
          },
          {
            deepKey: 'features.1',
            value: bannersData.allAccessYearlyBanner[lang].features[1]
              .replace(/{currency}/gi, currency.symbol)
              .replace(/{fullPrice}/gi, country === 'NL' ? '56.87' : '47'),
          },
        ],
      )

      const allAccessExtendYearlyBanner = overwriteBannerWithData(
        bannersData.allAccessYearlyExtendBanner[lang],
        [
          {
            deepKey: 'headline',
            value: bannersData.allAccessYearlyExtendBanner[lang].headline
              .replace(/{currency}/gi, currency.symbol)
              .replace(/{price}/gi, country === 'NL' ? '4.74' : '3.99'),
          },
          {
            deepKey: 'features.0',
            value: bannersData.allAccessYearlyExtendBanner[lang].features[0]
              .replace(/{currency}/gi, currency.symbol)
              .replace(/{price}/gi, country === 'NL' ? '4.74' : '3.99')
              .replace(/{fullPrice}/gi, country === 'NL' ? '56.87' : '47'),
          },
        ],
      )

      const studioAiYearlyTrialBanner = overwriteBannerWithData(
        bannersData.studioAiYearlyTrialBanner[lang],
        [
          {
            deepKey: 'buttonText',
            value: bannersData.studioAiYearlyTrialBanner[lang].buttonText
              .replace(/{currency}/gi, currency.symbol)
              .replace(/{price}/gi, '0.00'),
          },
          {
            deepKey: 'headline',
            value: bannersData.studioAiYearlyTrialBanner[lang].headline.replace(
              /{currency}/gi,
              currency.symbol,
            ),
          },
          {
            deepKey: 'features.1',
            value: bannersData.studioAiYearlyTrialBanner[lang].features[1]
              .replace(/{currency}/gi, currency.symbol)
              .replace(/{price}/gi, country === 'NL' ? '6.04' : '4.99')
              .replace(/{fullPrice}/gi, country === 'NL' ? '35.09' : '29.00'),
          },
        ],
      )

      //================================END================================//

      // LOGGED OUT VARIANTS
      const loggedOutConditions = [LOGGED_OUT, withCountry(country), withCurrency(currency.code)]
      const loggedOutId = `loggedOut-${country}-${currency.code}`

      // All Access Yearly Trial Banner
      const logoutVariant = {
        id: `${loggedOutId}-default`,
        content: (
          <AllAccessYearlyTrial
            banner={allAccessYearlyTrialBanner}
            allAccessYearlyBanner={allAccessYearlyBanner}
            showCountdown={country !== 'NL'}
          />
        ),
        conditions: loggedOutConditions,
        isDefault: currency.code === DEFAULT_CURRENCY.code && country === DEFAULT_COUNTRY_VARIANT,
      }

      variants.push(logoutVariant)

      // TRIAL VARIANTS
      const trialConditions = [
        LOGGED_IN,
        IN_YEARLY_TRIAL,
        withCountry(country),
        withCurrency(currency.code),
      ]

      variants.push({
        id: `yearly-trial-${country}-${currency.code}`,
        content: <AllAccessYearly banner={allAccessYearlyBanner} />,
        conditions: trialConditions,
      })

      // YEARLY VARIANTS

      const thresholdDate = new Date()
      thresholdDate.setDate(thresholdDate.getDate() + 430) // 430 days from now

      const yearlyConditions = [
        LOGGED_IN,
        IN_YEARLY,
        withCountry(country),
        withCurrency(currency.code),
      ]
      const yearlyId = `yearly-${country}-${currency.code}`

      // Studio Ai Trial
      variants.push({
        id: `${yearlyId}-studio-ai-trial-true-1-50`,
        content: <StudioAiYearlyTrial banner={studioAiYearlyTrialBanner} />,
        conditions: [
          NOT_IN_STUDIO_AI_ACTIVE_OR_TRIAL,
          ...yearlyConditions,
          {
            type: 'cookie-date',
            cookieName: ALL_ACCESS_NEXT_PAYMENT_DATE_COOKIE_NAME,
            operator: 'BEFORE',
            expectedValue: thresholdDate.toISOString(),
          },
          {
            type: 'randomize',
            interval: [1, 50],
          },
        ],
      })

      variants.push({
        id: `${yearlyId}-studio-ai-trial-true-51-100`,
        content: <AllAccessYearlyExtend banner={allAccessExtendYearlyBanner} />,
        conditions: [
          NOT_IN_STUDIO_AI_ACTIVE_OR_TRIAL,
          ...yearlyConditions,
          {
            type: 'cookie-date',
            cookieName: ALL_ACCESS_NEXT_PAYMENT_DATE_COOKIE_NAME,
            operator: 'BEFORE',
            expectedValue: thresholdDate.toISOString(),
          },
          {
            type: 'randomize',
            interval: [51, 100],
          },
        ],
      })

      variants.push({
        id: `${yearlyId}`,
        content: <AllAccessYearlyExtend banner={allAccessExtendYearlyBanner} />,
        conditions: [
          ...yearlyConditions,
          {
            type: 'cookie-date',
            cookieName: ALL_ACCESS_NEXT_PAYMENT_DATE_COOKIE_NAME,
            operator: 'BEFORE',
            expectedValue: thresholdDate.toISOString(),
          },
        ],
      })

      variants.push({
        id: `studio-ai-yearly-trial-${country}-${currency.code}`,
        content: <StudioAiYearlyTrial banner={studioAiYearlyTrialBanner} />,
        conditions: [...yearlyConditions, NOT_IN_STUDIO_AI_ACTIVE_OR_TRIAL],
      })

      variants.push({
        id: `${yearlyId}-no-banner`,
        content: <div />,
        conditions: [
          ...yearlyConditions,
          {
            type: 'cookie-date',
            cookieName: ALL_ACCESS_NEXT_PAYMENT_DATE_COOKIE_NAME,
            operator: 'AFTER',
            expectedValue: thresholdDate.toISOString(),
          },
        ],
      })

      // LOGGED IN DEFAULT VARIANT
      const defaultLoggedInConditions = [
        LOGGED_IN,
        NOT_IN_MONTHLY,
        withCountry(country),
        withCurrency(currency.code),
      ]
      const defaultLoggedInId = `loggedin-${country}-${currency.code}`

      // Logged in default
      variants.push({
        id: defaultLoggedInId,
        content: (
          <AllAccessYearlyTrial
            banner={allAccessYearlyTrialBanner}
            allAccessYearlyBanner={allAccessYearlyBanner}
            showCountdown={country !== 'NL'}
          />
        ),
        conditions: defaultLoggedInConditions,
      })
    }
  }

  // For monthly subscribers, show no banner
  variants.push({
    id: `no-banner-monthly`,
    content: <div />,
    conditions: [LOGGED_IN, IN_MONTHLY],
  })

  return variants
}

export const getVariantsConfig = (locale: Language): VariantsConfig => {
  return {
    variants: getVariants(locale),
    fallbackCookies: {
      [COUNTRY_COOKIE_NAME]: 'US',
      [CURRENCY_COOKIE_NAME]: 'usd',
    },
  }
}

interface Currency {
  code: CurrencyCode
  symbol: string
}

export const CURRENCIES = [
  { code: CurrencyCode.Usd, symbol: '$' },
  { code: CurrencyCode.Eur, symbol: '€' },
  { code: CurrencyCode.Gbp, symbol: '£' },
] as Currency[]
const COUNTRIES = ['NL', 'other'] as const

export function withCurrency(currency: string): Condition {
  return {
    type: 'cookie',
    cookieName: CURRENCY_COOKIE_NAME,
    expectedValue: currency.toUpperCase(),
    operator: 'EQUALS',
  }
}

function withCountry(country: CountryVariant): Condition {
  return country === 'other' ? NOT_NL_COUNTRY : NL_COUNTRY
}

const DEFAULT_CURRENCY: Currency = { code: CurrencyCode.Usd, symbol: '$' }

type CountryVariant = (typeof COUNTRIES)[number]
const DEFAULT_COUNTRY_VARIANT: CountryVariant = 'other'

interface ExperimentOverwrite {
  deepKey: string
  value: string
}

//eslint-disable-next-line
export function overwriteBannerWithData<T extends { type: string }>(
  controlBannerData: T,
  experimentsOverwrites: ExperimentOverwrite[],
): T {
  if (experimentsOverwrites.length === 0) {
    throw new Error(`No overwrite was provided for the controlBannerData. Cannot apply overwrites.`)
  }

  // Clone the initial object to avoid mutation
  const toBeReturnedData: T = cloneDeep(controlBannerData)

  experimentsOverwrites.forEach(overwrite => {
    objectPath.set(toBeReturnedData, overwrite.deepKey, overwrite.value)
  })

  return toBeReturnedData
}
